import { cn } from '../../utils/cn';

export type DayCode = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';
export type TimeBand = 'morning' | 'afternoon' | 'evening' | 'night';

export interface DayTimeBandSelectorValue {
  readonly days: ReadonlySet<DayCode>;
  readonly bands: ReadonlySet<TimeBand>;
}

export interface DayTimeBandSelectorProps {
  readonly value: DayTimeBandSelectorValue;
  readonly onChange: (next: DayTimeBandSelectorValue) => void;
  readonly weekStartsOn?: 0 | 1;
  readonly daysLabel?: string;
  readonly bandsLabel?: string;
  readonly disabled?: boolean;
  readonly className?: string;
}

const DAY_LABELS: Record<DayCode, string> = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sun',
};

const WEEK_MON_FIRST: readonly DayCode[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const WEEK_SUN_FIRST: readonly DayCode[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

const BAND_LABELS: Record<TimeBand, { title: string; range: string }> = {
  morning: { title: 'Morning', range: '6–12' },
  afternoon: { title: 'Afternoon', range: '12–18' },
  evening: { title: 'Evening', range: '18–22' },
  night: { title: 'Night', range: '22–6' },
};

const BANDS: readonly TimeBand[] = ['morning', 'afternoon', 'evening', 'night'];

export function DayTimeBandSelector({
  value,
  onChange,
  weekStartsOn = 1,
  daysLabel = 'Days',
  bandsLabel = 'Time of day',
  disabled,
  className,
}: DayTimeBandSelectorProps) {
  const week = weekStartsOn === 0 ? WEEK_SUN_FIRST : WEEK_MON_FIRST;

  const toggleDay = (d: DayCode) => {
    if (disabled) return;
    const next = new Set(value.days);
    if (next.has(d)) next.delete(d);
    else next.add(d);
    onChange({ days: next, bands: value.bands });
  };

  const toggleBand = (b: TimeBand) => {
    if (disabled) return;
    const next = new Set(value.bands);
    if (next.has(b)) next.delete(b);
    else next.add(b);
    onChange({ days: value.days, bands: next });
  };

  return (
    <div className={cn('flex flex-col gap-4', disabled && 'opacity-45', className)}>
      <section className="flex flex-col gap-2">
        <h4 className="text-[10.5px] font-bold uppercase tracking-wider text-ink-3 font-mono">
          {daysLabel}
        </h4>
        <div role="group" aria-label={daysLabel} className="grid grid-cols-7 gap-1.5">
          {week.map((d) => {
            const active = value.days.has(d);
            return (
              <button
                key={d}
                type="button"
                onClick={() => toggleDay(d)}
                aria-pressed={active}
                disabled={disabled}
                className={cn(
                  'h-10 rounded-md text-[11px] font-bold uppercase tracking-wider',
                  'transition-colors duration-[120ms] border',
                  'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-accent/25',
                  active
                    ? 'bg-primary text-primary-ink border-accent'
                    : 'bg-surface text-ink-2 border-hairline-2 hover:bg-surface-2',
                  disabled && 'cursor-not-allowed',
                )}
              >
                {DAY_LABELS[d]}
              </button>
            );
          })}
        </div>
      </section>
      <section className="flex flex-col gap-2">
        <h4 className="text-[10.5px] font-bold uppercase tracking-wider text-ink-3 font-mono">
          {bandsLabel}
        </h4>
        <div role="group" aria-label={bandsLabel} className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {BANDS.map((b) => {
            const active = value.bands.has(b);
            const meta = BAND_LABELS[b];
            return (
              <button
                key={b}
                type="button"
                onClick={() => toggleBand(b)}
                aria-pressed={active}
                disabled={disabled}
                className={cn(
                  'flex flex-col items-start gap-0.5 p-2.5 rounded-lg border',
                  'transition-colors duration-[120ms]',
                  'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-accent/25',
                  active
                    ? 'bg-accent-soft border-accent text-accent'
                    : 'bg-surface border-hairline-2 hover:bg-surface-2 text-ink',
                  disabled && 'cursor-not-allowed',
                )}
              >
                <span className="text-[12.5px] font-bold">{meta.title}</span>
                <span className="text-[10px] font-mono opacity-70">{meta.range}</span>
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
}
